import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  FiPlay,
  FiAlertCircle,
  FiCheckCircle,
  FiRefreshCw,
  FiShield,
  FiEdit2,
  FiPlus,
  FiTrash2,
  FiClock,
} from "react-icons/fi";
import { BackButton } from "../../components";
import { useStateContext } from "../../contexts/ContextProvider";
import useKYCStatusRedirect from "../../hooks/useKYCStatusRedirect";
import { userRequest } from "../../utils/requestMethods";
import { formatCurrency } from "../../utils/currencyUtils";
import StatCard from "../../components/common/StatCard";
import ButtonLoading from "../../components/ButtonLoading";
import Modal from "../../components/common/Modal";

const statusMeta = {
  successful: { bg: "bg-green-100", text: "text-green-700", label: "Successful" },
  partially_successful: {
    bg: "bg-yellow-100",
    text: "text-yellow-700",
    label: "Partially Successful",
  },
  failed: { bg: "bg-red-100", text: "text-red-700", label: "Failed" },
  needs_review: { bg: "bg-orange-100", text: "text-orange-800", label: "Needs Review" },
  processing: { bg: "bg-blue-100", text: "text-blue-700", label: "Processing" },
  pending: { bg: "bg-gray-100", text: "text-gray-600", label: "Pending" },
};

const POLL_INTERVAL_MS = 30000;

const SalaryBatchDetail = ({ history }) => {
  useKYCStatusRedirect("/finance/payroll/batches");
  const { batchId } = useParams();
  const { user, school } = useStateContext();
  const schoolId = school?._id;
  const isOwner = user?.role === 0;

  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [confirmingExecution, setConfirmingExecution] = useState(false);
  const [error, setError] = useState("");
  const [polling, setPolling] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editingAdjustments, setEditingAdjustments] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const pollIntervalRef = useRef(null);

  const fetchReview = async () => {
    if (!schoolId || !batchId) return;
    try {
      const { data } = await userRequest.get(`/schools/${schoolId}/salary-batches/${batchId}`);
      setReview(data);
      return data;
    } catch (err) {
      console.log(err);
    }
  };

  const pollUntilDone = () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    setPolling(true);
    pollIntervalRef.current = setInterval(async () => {
      if (document.visibilityState === "hidden") return;
      const data = await fetchReview();
      const status = data?.batch?.status;
      if (status !== "processing") {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setPolling(false);
      }
    }, POLL_INTERVAL_MS);
  };

  useEffect(() => {
    setLoading(true);
    fetchReview()
      .then((data) => {
        if (data?.batch?.status === "processing") {
          pollUntilDone();
        }
      })
      .finally(() => setLoading(false));

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, batchId]);

  const handleExecute = async () => {
    setExecuting(true);
    setError("");
    try {
      await userRequest.post(`/schools/${schoolId}/salary-batches/${batchId}/execute`);
      await fetchReview();
      pollUntilDone();
      setConfirmingExecution(false);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to execute batch. Please try again.");
    } finally {
      setExecuting(false);
    }
  };

  const getStaffId = (payment) => payment?.staff_id?._id || payment?.staff_id;

  const openPaymentEditor = (payment) => {
    setEditingPayment(payment);
    const manualAdjustments = (payment.adjustments || []).filter(
      (a) => a.source !== "lateness_penalty",
    );
    setEditingAdjustments(manualAdjustments.map((adjustment) => ({ ...adjustment })));
    setError("");
  };

  const updateAdjustment = (index, field, value) => {
    setEditingAdjustments((current) =>
      current.map((adjustment, itemIndex) =>
        itemIndex === index ? { ...adjustment, [field]: value } : adjustment,
      ),
    );
  };

  const savePaymentEdit = async () => {
    setSavingEdit(true);
    setError("");
    try {
      const editedStaffId = String(getStaffId(editingPayment));
      await userRequest.patch(`/schools/${schoolId}/salary-batches/${batchId}`, {
        staff: payments.map((payment) => ({
          staff_id: String(getStaffId(payment)),
          adjustments:
            String(getStaffId(payment)) === editedStaffId
              ? editingAdjustments
                  .filter((adjustment) => adjustment.source !== "lateness_penalty")
                  .map((adjustment) => ({
                    type: adjustment.type,
                    label: adjustment.label.trim(),
                    amount: Number(adjustment.amount),
                  }))
              : (payment.adjustments || [])
                  .filter((adjustment) => adjustment.source !== "lateness_penalty")
                  .map((adjustment) => ({
                    type: adjustment.type,
                    label: adjustment.label,
                    amount: Number(adjustment.amount),
                  })),
        })),
      });
      await fetchReview();
      setEditingPayment(null);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update salary batch. Please try again.");
    } finally {
      setSavingEdit(false);
    }
  };

  const batch = review?.batch;
  const payments = review?.payments || [];
  const s = statusMeta[batch?.status] || statusMeta.pending;
  const canExecute = isOwner && batch?.status === "pending" && review?.sufficientBalance;
  const feeLabel =
    batch?.status === "pending" || batch?.status === "processing"
      ? "Maximum Fee Reserved"
      : "Actual Fee Charged";

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-10 dark:bg-secondary-dark-bg bg-white rounded-3xl min-h-screen">
      <BackButton history={history} />

      {loading ? (
        <div className="py-16 text-center text-sm text-gray-400">Loading batch...</div>
      ) : !batch ? (
        <div className="py-16 text-center text-sm text-gray-400">Batch not found.</div>
      ) : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-gray-900">Payroll — {batch.payment_month}</h2>
                <span
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${s.bg} ${s.text}`}
                >
                  {s.label}
                  {polling ? " (checking...)" : ""}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Fund: {review?.fund?.name || batch.fund_id}
              </p>
            </div>
            <button
              type="button"
              disabled={!canExecute || executing}
              title={
                !isOwner
                  ? "Only the school owner can execute payroll"
                  : !review?.sufficientBalance
                    ? "Insufficient fund balance"
                    : ""
              }
              onClick={() => setConfirmingExecution(true)}
              className="inline-flex items-center gap-2 py-2.5 px-5 text-sm font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {executing ? (
                <ButtonLoading styles="text-white bg-indigo-500 font-medium rounded-full text-sm px-5 py-1" />
              ) : (
                <>
                  <FiPlay className="w-4 h-4" /> Execute
                </>
              )}
            </button>
          </div>

          {error && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm mb-6">
              <FiAlertCircle className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!review?.sufficientBalance && batch.status === "pending" && (
            <div className="flex items-start gap-3 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-xl px-4 py-3 text-sm mb-6">
              <FiAlertCircle className="mt-0.5 shrink-0" />
              <span>
                Insufficient fund balance to execute this batch. Available:{" "}
                {formatCurrency(review?.availableBalance)}, required:{" "}
                {formatCurrency(batch.grand_total || batch.total_amount)}.
              </span>
            </div>
          )}

          {batch.status === "processing" && (
            <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 text-blue-800 rounded-xl px-4 py-3 text-sm mb-6">
              <FiRefreshCw className="mt-0.5 shrink-0" />
              <span>
                Payroll has been sent for processing. This page refreshes every 30 seconds while it
                is open. Do not submit this batch again.
              </span>
            </div>
          )}

          {batch.status === "needs_review" && (
            <div className="flex items-start gap-3 bg-orange-50 border border-orange-200 text-orange-900 rounded-xl px-4 py-3 text-sm mb-6">
              <FiShield className="mt-0.5 shrink-0" />
              <span>
                Bank confirmation is delayed. The payroll must not be rerun. A finance administrator
                will resolve it once bank evidence is available.
              </span>
            </div>
          )}

          {(batch.status === "successful" || batch.status === "partially_successful") && (
            <div className="flex items-start gap-3 bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm mb-6">
              <FiCheckCircle className="mt-0.5 shrink-0" />
              <span>
                Payroll executed on{" "}
                {batch.executed_at ? new Date(batch.executed_at).toLocaleString("en-NG") : "—"}.
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
            <StatCard
              label="Net Salaries"
              value={formatCurrency(batch.total_amount)}
              color="indigo"
            />
            <StatCard
              label={feeLabel}
              value={batch.fee_amount ? formatCurrency(batch.fee_amount) : "—"}
              color="blue"
            />
            <StatCard
              label="Total Debit"
              value={formatCurrency(batch.grand_total || batch.total_amount)}
              color="purple"
            />
            <StatCard
              label="Available Balance"
              value={formatCurrency(review?.availableBalance)}
              color={review?.sufficientBalance ? "green" : "red"}
            />
          </div>

          <div className="border border-gray-200 rounded-2xl overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase px-5 py-3">
                    Recipient
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase px-5 py-3">
                    Base Salary
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase px-5 py-3">
                    Adjustments
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase px-5 py-3">
                    Net Pay
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase px-5 py-3">
                    Status
                  </th>
                  {batch.status === "pending" && (
                    <th className="text-right text-xs font-semibold text-gray-600 uppercase px-5 py-3">
                      Action
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payments.length === 0 ? (
                  <tr>
                    <td
                      colSpan={batch.status === "pending" ? 6 : 5}
                      className="px-5 py-10 text-center text-sm text-gray-400"
                    >
                      No recipients found.
                    </td>
                  </tr>
                ) : (
                  payments.map((p, idx) => {
                    const base = p.base_salary || p.amount || p.monthly_salary;
                    const adjustments = p.adjustments || [];
                    const bonuses = adjustments
                      .filter((a) => a.type === "bonus")
                      .reduce((sum, a) => sum + (a.amount || 0), 0);
                    const deductions = adjustments
                      .filter((a) => a.type === "deduction")
                      .reduce((sum, a) => sum + (a.amount || 0), 0);

                    return (
                      <React.Fragment key={p._id || p.staff_id || idx}>
                        <tr className="hover:bg-gray-50/50">
                          <td className="px-5 py-3 text-sm text-gray-700 font-medium font-sans">
                            {p.account_name ||
                              (p.firstname ? `${p.firstname} ${p.lastname}` : p.name || p.staff_id)}
                          </td>
                          <td className="px-5 py-3 text-sm text-gray-600">
                            {formatCurrency(base)}
                          </td>
                          <td className="px-5 py-3 text-sm">
                            {adjustments.length === 0 ? (
                              <span className="text-gray-400 text-xs">—</span>
                            ) : (
                              <div className="flex flex-wrap gap-1 text-xs">
                                {bonuses > 0 && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                                    +{formatCurrency(bonuses)}
                                  </span>
                                )}
                                {deductions > 0 && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                                    -{formatCurrency(deductions)}
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-3 text-sm font-semibold text-gray-900">
                            {formatCurrency(p.amount)}
                          </td>
                          <td className="px-5 py-3 text-sm">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                                p.status === "success"
                                  ? "bg-green-100 text-green-700"
                                  : p.status === "failed"
                                    ? "bg-red-100 text-red-700"
                                    : p.status === "processing"
                                      ? "bg-blue-100 text-blue-700"
                                      : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {p.status || "pending"}
                            </span>
                            {p.failure_reason && (
                              <p className="mt-1 text-xs text-red-600">{p.failure_reason}</p>
                            )}
                          </td>
                          {batch.status === "pending" && (
                            <td className="px-5 py-3 text-right">
                              <button
                                type="button"
                                onClick={() => openPaymentEditor(p)}
                                className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                              >
                                <FiEdit2 /> Edit
                              </button>
                            </td>
                          )}
                        </tr>
                        {adjustments.length > 0 && (
                          <tr className="bg-gray-50/60 text-xs text-gray-600">
                            <td colSpan={batch.status === "pending" ? 6 : 5} className="px-5 py-2">
                              <div className="flex flex-wrap items-center gap-2 pl-2 border-l-2 border-indigo-300">
                                <span className="font-semibold text-gray-500">Breakdown:</span>
                                {adjustments.map((adj, aIdx) => {
                                  const isLateness = adj.source === "lateness_penalty";
                                  return (
                                    <span
                                      key={aIdx}
                                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                        isLateness
                                          ? "bg-amber-50 text-amber-800 border border-amber-200"
                                          : adj.type === "bonus"
                                            ? "text-green-700 bg-green-50/50"
                                            : "text-red-700 bg-red-50/50"
                                      }`}
                                    >
                                      {isLateness && (
                                        <FiClock className="w-3 h-3 text-amber-600 shrink-0" />
                                      )}
                                      <span>
                                        {adj.type === "bonus" ? "+" : "-"}
                                        {formatCurrency(adj.amount)} ({adj.label})
                                      </span>
                                      {isLateness && (
                                        <span className="text-[10px] uppercase font-bold text-amber-600 bg-amber-100 px-1 rounded">
                                          Auto
                                        </span>
                                      )}
                                    </span>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <Modal
            isOpen={confirmingExecution}
            onClose={() => !executing && setConfirmingExecution(false)}
            title="Confirm payroll execution"
            icon={<FiShield className="text-indigo-600 text-lg" />}
            footer={
              <div className="flex gap-3">
                <button
                  type="button"
                  disabled={executing}
                  onClick={() => setConfirmingExecution(false)}
                  className="flex-1 rounded-full border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={executing}
                  onClick={handleExecute}
                  className="flex-1 rounded-full bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {executing ? "Starting payroll…" : "Confirm & Execute"}
                </button>
              </div>
            }
          >
            <div className="space-y-3 text-sm text-gray-700">
              <p>
                You are about to send payroll for{" "}
                <strong>
                  {payments.length} staff member{payments.length === 1 ? "" : "s"}
                </strong>
                .
              </p>
              <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4 space-y-2">
                <div className="flex justify-between">
                  <span>Net salaries</span>
                  <strong>{formatCurrency(batch.total_amount)}</strong>
                </div>
                <div className="flex justify-between">
                  <span>Maximum fee reserve</span>
                  <strong>{formatCurrency(batch.fee_amount || 0)}</strong>
                </div>
                <div className="flex justify-between border-t border-indigo-100 pt-2 text-indigo-900">
                  <span>Total fund debit</span>
                  <strong>{formatCurrency(batch.grand_total || batch.total_amount)}</strong>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                The final fee is ₦100 for each staff payment confirmed as settled. Do not submit
                this payroll again after confirming.
              </p>
            </div>
          </Modal>

          <Modal
            isOpen={!!editingPayment}
            onClose={() => !savingEdit && setEditingPayment(null)}
            title={`Edit ${editingPayment?.account_name || "salary payment"}`}
            icon={<FiEdit2 className="text-indigo-600 text-lg" />}
            footer={
              <div className="flex gap-3">
                <button
                  type="button"
                  disabled={savingEdit}
                  onClick={() => setEditingPayment(null)}
                  className="flex-1 rounded-full border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={
                    savingEdit ||
                    editingAdjustments.some(
                      (item) => !item.label?.trim() || Number(item.amount) <= 0,
                    )
                  }
                  onClick={savePaymentEdit}
                  className="flex-1 rounded-full bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {savingEdit ? "Saving…" : "Save changes"}
                </button>
              </div>
            }
          >
            <div className="space-y-4">
              {/* Automatic Lateness Deductions (Read-only) */}
              {editingPayment?.adjustments?.some((a) => a.source === "lateness_penalty") && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-3.5 space-y-2">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-900">
                    <FiClock className="w-3.5 h-3.5 text-amber-700 shrink-0" />
                    <span>System Lateness & Absence Deductions (Automatic)</span>
                  </div>
                  {editingPayment.adjustments
                    .filter((a) => a.source === "lateness_penalty")
                    .map((adj, idx) => (
                      <div
                        key={idx}
                        className="flex justify-between items-center text-xs text-amber-900 bg-white/90 px-3 py-2 rounded-lg border border-amber-100 shadow-xs"
                      >
                        <span className="font-medium">{adj.label}</span>
                        <span className="font-bold text-amber-900">
                          -{formatCurrency(adj.amount)}
                        </span>
                      </div>
                    ))}
                  <p className="text-[11px] text-amber-800">
                    Lateness penalty deductions are calculated automatically and can only be
                    adjusted by waiving the underlying penalty on the Teacher Attendance page.
                  </p>
                </div>
              )}

              {/* Manual Adjustments Header */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-800">Manual adjustments</p>
                  <p className="text-xs text-gray-500">
                    Add custom bonuses or deductions before executing payroll.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setEditingAdjustments((current) => [
                      ...current,
                      { type: "bonus", label: "", amount: "" },
                    ])
                  }
                  className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                >
                  <FiPlus /> Add
                </button>
              </div>
              {editingAdjustments.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 py-8 text-center text-sm text-gray-400">
                  No manual adjustments for this staff member.
                </div>
              ) : (
                editingAdjustments.map((adjustment, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-center">
                    <select
                      value={adjustment.type}
                      onChange={(event) => updateAdjustment(index, "type", event.target.value)}
                      className="col-span-3 rounded-xl border-gray-200 text-sm"
                    >
                      <option value="bonus">Bonus</option>
                      <option value="deduction">Deduction</option>
                    </select>
                    <input
                      value={adjustment.label}
                      onChange={(event) => updateAdjustment(index, "label", event.target.value)}
                      placeholder="Description"
                      maxLength={100}
                      className="col-span-5 rounded-xl border-gray-200 text-sm"
                    />
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={adjustment.amount}
                      onChange={(event) => updateAdjustment(index, "amount", event.target.value)}
                      placeholder="Amount"
                      className="col-span-3 rounded-xl border-gray-200 text-sm"
                    />
                    <button
                      type="button"
                      aria-label="Remove adjustment"
                      onClick={() =>
                        setEditingAdjustments((current) =>
                          current.filter((_, itemIndex) => itemIndex !== index),
                        )
                      }
                      className="col-span-1 p-2 text-red-500 hover:text-red-700"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ))
              )}
            </div>
          </Modal>
        </>
      )}
    </div>
  );
};

export default SalaryBatchDetail;
